import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Crown, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Countdown } from "@/components/DiwaliBanner";
import { Spinner } from "@/components/Loader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiError, inr } from "@/lib/api";
import { openRazorpay } from "@/lib/razorpay";
import { useAuth } from "@/context/AuthContext";

function DiwaliHero({ offer }) {
  return (
    <div className="relative overflow-hidden rounded-3xl bg-tm-dark p-8 text-white tm-gold-glow lg:p-12" data-testid="pricing-diwali-hero">
      <img src="/images/diwali.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
      <div className="absolute inset-0 bg-gradient-to-r from-tm-dark via-tm-dark/80 to-tm-dark/30" />
      <div className="relative grid items-center gap-8 lg:grid-cols-[1fr_auto]">
        <div>
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-tm-saffron"><Sparkles className="h-4 w-4" />{offer.title}</p>
          <h1 className="mt-3 font-display text-4xl font-extrabold leading-tight sm:text-5xl">Free until<br />{offer.deadline}</h1>
          <p className="mt-3 max-w-lg text-white/75" data-testid="pricing-offer-note">{offer.note}</p>
        </div>
        <div><p className="mb-2 text-xs uppercase tracking-widest text-white/60">Offer ends in</p><Countdown endsAt={offer.deadline_iso} /></div>
      </div>
    </div>
  );
}

function PlanCard({ plan, current, onBuy, busy }) {
  return (
    <div className={`relative flex flex-col rounded-3xl border bg-white p-8 tm-lift ${plan.badge ? "border-tm-saffron tm-gold-glow" : ""}`} data-testid={`plan-card-${plan.id}`}>
      {plan.badge && <span className="absolute -top-3 left-8 rounded-full bg-tm-saffron px-3 py-1 text-xs font-bold text-tm-dark">{plan.badge}</span>}
      <h3 className="font-display text-xl font-bold text-tm-green">{plan.label}</h3>
      <div className="mt-4 flex items-end gap-2">
        <span className="font-display text-4xl font-extrabold text-tm-green" data-testid={`plan-price-${plan.id}`}>{inr(plan.charged_now ?? plan.current_price ?? plan.price)}</span>
        <span className="pb-1 text-sm text-slate-500">/ {plan.days >= 365 ? "year" : "month"}</span>
      </div>
      <p className="mt-1 text-xs text-slate-500">{plan.note}</p>
      {plan.post_nov_price > plan.current_price && <p className="mt-1 text-sm"><span className="text-slate-400 line-through" data-testid={`plan-later-price-${plan.id}`}>{inr(plan.post_nov_price)}</span> <span className="font-semibold text-tm-saffron">price after offer</span></p>}
      <ul className="mt-6 space-y-3 text-sm text-slate-700">
        {(plan.features || []).map((f) => <li key={f} className="flex gap-2.5"><Check className="h-4 w-4 shrink-0 text-tm-green-light" />{f}</li>)}
      </ul>
      <div className="mt-auto pt-8">
        <Button onClick={() => onBuy(plan)} disabled={busy} className="w-full rounded-full bg-tm-green hover:bg-tm-green/90" data-testid={`plan-buy-${plan.id}`}>
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Crown className="mr-2 h-4 w-4" />}{current ? "Extend membership" : "Subscribe now"}
        </Button>
      </div>
    </div>
  );
}

export default function Pricing() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [promo, setPromo] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => (user ? api.get("/membership") : api.get("/membership/plans")).then(({ data }) => setInfo(data)).catch(() => setInfo({ plans: [] }));
  useEffect(() => {
    if (user !== null) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const buy = async (plan) => {
    if (!user) return navigate("/login", { state: { from: "/pricing" } });
    setBusy(true);
    try {
      const { data: order } = await api.post("/payments/orders", { plan: plan.id, promo_code: promo || null });
      const resp = await openRazorpay(order);
      if (resp) {
        await api.post("/payments/verify", resp);
        toast.success("Payment successful. Your membership is active!");
        await refreshUser();
        load();
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!info) return <Spinner />;
  return (
    <div className="mx-auto max-w-5xl space-y-10">
      {info.offer?.trial_active && <DiwaliHero offer={info.offer} />}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="tm-eyebrow">Membership</p>
          <h2 className="mt-2 font-display text-2xl font-bold text-tm-green sm:text-3xl">Choose your plan</h2>
          {info.active && <p className="mt-2 text-sm font-medium text-emerald-700" data-testid="membership-active-note">Your membership is active{info.expiry && ` until ${new Date(info.expiry).toLocaleDateString("en-IN")}`}.</p>}
          {!user && <p className="mt-2 text-sm text-slate-500">Showing professional plans. Students see student pricing after login.</p>}
        </div>
        {user && <Input value={promo} onChange={(e) => setPromo(e.target.value.toUpperCase())} placeholder="Promo code" className="h-11 w-48 rounded-full bg-white" data-testid="pricing-promo-input" />}
      </div>
      <div className="grid gap-6 md:grid-cols-2">
        {(info.plans || []).map((p) => <PlanCard key={p.id} plan={p} current={info.active} onBuy={buy} busy={busy} />)}
      </div>
      {info.payments_enabled === false && <p className="text-sm text-amber-700">Online payments are temporarily disabled.</p>}
    </div>
  );
}
